import React, { type ErrorInfo, PropsWithChildren } from "react";
import { IResourcesStore } from "../../resources/ResourcesStore";
import { injectFromApp } from "../inject";
import { DobButton } from "@dedalusdiit/dob-components";
import { DiagnosticProperty, ILogger } from "../../util/exceptions/ExceptionlessStore";
import { notNullOrUndefined } from "../../util/functions/notNullOrUndefined";

export interface IOopsPageErrorCatchingComponentProps {
	isLandingPage: boolean;
	statusCode?: number;
	isLightThemed?: boolean;
	error?: Error | null;
	resourcesStore?: IResourcesStore;
	logger?: ILogger;
}

export interface IOopsPageErrorCatchingComponentState {
	error: Error | null;
	isCollapsed: boolean;
}

@injectFromApp<IOopsPageErrorCatchingComponentProps>(stores => ({
	resourcesStore: stores.resourcesStore,
	logger: stores.logger,
}))
export class OopsPageErrorCatchingComponent extends React.Component<PropsWithChildren<IOopsPageErrorCatchingComponentProps>, IOopsPageErrorCatchingComponentState> {
	constructor(props: PropsWithChildren<IOopsPageErrorCatchingComponentProps>) {
		super(props);
		this.state = {
			error: null,
			isCollapsed: true,
		};
	}

	componentDidMount() {
		const { error, logger } = this.props;

		if (notNullOrUndefined(error)) {
			console.error("😔 Oops page was rendered with the following error: ", error?.name, error?.message, error?.stack, error);
			logger?.logException(error, true);
		}
	}

	/**
	 * This lifecycle method is automatically invoked after an error has been thrown by a descendant component - thanks React!
	 * @param error
	 */
	static getDerivedStateFromError(error: Error) {
		// Update state so the next render will show the fallback UI.
		return { error: error };
	}

	getMessage(): string {
		const { props } = this;
		const { statusCode, resourcesStore } = props;

		switch (statusCode) {
			case 404:
				return resourcesStore?.resources.PageNotFound ?? "The page you are looking for does not exist";
			case 500:
				return resourcesStore?.resources.SomethingWentWrong ?? "Something went wrong.";
			default:
				return resourcesStore?.resources.SomethingWentWrong ?? "Something went wrong.";
		}
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const { logger } = this.props;
		logger?.logException(error, true, [new DiagnosticProperty("errorInfo", errorInfo)], []);
		console.error("😔 Oops page was rendered with the following error: ", error?.name, error?.message, error?.stack, error, errorInfo);
	}

	onClickErrorHeader = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState(previousState => ({ isCollapsed: !previousState.isCollapsed }));
	};

	onDblClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	onClickRefresh = (e: React.MouseEvent<HTMLButtonElement>) => {
		window.location.reload();
	};

	onClickSupport = (e: React.MouseEvent<HTMLButtonElement>) => {
		window.location.href = "/POW3/Support";
	};

	onSelectCompleteErrorMessage = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!this.state.isCollapsed) {
			const selection = window.getSelection();
			const range = document.createRange();

			range.selectNodeContents(e.currentTarget);
			selection?.removeAllRanges();
			selection?.addRange(range);
		}
	};

	render() {
		const { props, state } = this;
		const { error: errorSetByState, isCollapsed } = state;
		const { error: errorSetByProps, children, isLandingPage, isLightThemed, resourcesStore } = props;

		const error = errorSetByState ?? errorSetByProps;

		// If this component has children, and there is no error, render the children.
		if (!error && children) {
			return children;
		}

		let errorContainer: React.JSX.Element | null = null;

		if (error) {
			const stack = error.stack;
			const hasStackTrace = notNullOrUndefined(stack) && stack.trim() !== "";
			const body =
				!isCollapsed && hasStackTrace ? (
					<div className="error-body">
						<div className="stack">
							<pre>{stack}</pre>
						</div>
					</div>
				) : null;

			const collapseIcon = isLightThemed ? (
				isCollapsed ? (
					<img src="/POW4/img/icons/icnArrowDownBlack.png" title="collapsed" alt="collapsed" />
				) : (
					<img src="/POW4/img/icons/icnArrowUpBlack.png" title="expanded" alt="expanded" />
				)
			) : isCollapsed ? (
				<img src="/POW4/img/icons/icnArrowDownWhite.png" title="collapsed" alt="collapsed" />
			) : (
				<img src="/POW4/img/icons/icnArrowUpWhite.png" title="expanded" alt="expanded" />
			);

			errorContainer = (
				<div className="oops-error-container" onClick={this.onSelectCompleteErrorMessage}>
					<div className="error-header" onClick={this.onClickErrorHeader}>
						<div className="name">{error.name}</div>
						<div className="message">{error.message}</div>
						{hasStackTrace && <div className="collapse">{collapseIcon}</div>}
					</div>
					{body}
				</div>
			);
		}

		return (
			<div className={`${isLandingPage ? "landing-page" : ""} ${isLightThemed ? "light-theme" : ""} error-catching-component`} onDoubleClick={this.onDblClick}>
				{isLandingPage && (
					<div id="header">
						<a href="/">
							<h1>PACSonWEB</h1>
						</a>
					</div>
				)}
				<div className="container">
					<div className="oops-container">
						<div className="oops">Oops</div>
						<div className="title">
							<h1>{this.getMessage()}</h1>
						</div>
						<div className="if-this-problem-persists">
							<p>{resourcesStore?.resources.SomethingWentWrongInfo ?? "If this problem persists, please contact our helpdesk."}</p>
						</div>
						<div className="buttons-section">
							<DobButton type="button" color="silent" outline={true} onClick={this.onClickRefresh}>
								{resourcesStore?.resources.RefreshPage ?? "Refresh page"}
							</DobButton>
							<DobButton type="button" color="primary" outline={true} onClick={this.onClickSupport}>
								{resourcesStore?.resources.GetSupport ?? "Get support"}
							</DobButton>
						</div>
					</div>
					{errorContainer}
				</div>
			</div>
		);
	}
}
